import { Mutex } from 'async-mutex';
import { secondsToMilliseconds } from 'date-fns';

import {
  AudioEventData,
  AudioEventDataList,
  RecordingMetadata,
  RecordingState,
} from '../provider/session-recording/types';

export const RECORDED_AUDIO_INTERVAL_MS = secondsToMilliseconds(30);

type RecordingResult = {
  audioBlob: Blob;
  audioURL: string;
  recordingMetadata: RecordingMetadata;
};

type OnIntervalProc = (recordingResult: RecordingResult) => void;

type MediaRecorderEventHandlers = Pick<MediaRecorder, 'onerror'>;

type StopRecordingOptions = {
  shouldClearInterval?: boolean;
};

type StartRecordingOptions = {
  onIntervalProc?: OnIntervalProc;
  intervalMs?: number;
};

function getDefaultRecordingMetadata(): RecordingMetadata {
  return {
    startTime: null,
    endTime: null,
  };
}

/**
 * Exposes methods to get and control audio recordings from a given media
 * stream. The instantiated object will maintain state related to recording
 * internally.
 *
 * Please stop recording before discarding the object.
 */
export class AudioRecorder {
  #audioEvents: AudioEventDataList = [];
  #controlRecordingMutex = new Mutex();
  #getRecordingMutex = new Mutex();
  #mediaRecorder: MediaRecorder;
  #recordingInterval: NodeJS.Timeout | null = null;
  #recordingMetadata: RecordingMetadata = getDefaultRecordingMetadata();

  constructor(
    sourceStream: MediaStream,
    mediaRecorderOptions: MediaRecorderOptions = {},
    eventHandlers?: MediaRecorderEventHandlers,
  ) {
    this.#mediaRecorder = this.#initMediaRecorder(sourceStream, mediaRecorderOptions, eventHandlers);
  }

  #clearAudioEvents() {
    this.#audioEvents = [];
  }

  #resetRecordingMetadata() {
    this.#recordingMetadata = getDefaultRecordingMetadata();
  }

  #pushAudioEventData(eventData: AudioEventData) {
    this.#audioEvents.push(eventData);
  }

  #initMediaRecorder(
    sourceStream: MediaStream,
    mediaRecorderOptions?: MediaRecorderOptions,
    eventHandlers?: MediaRecorderEventHandlers,
  ) {
    const mediaRecorder = new MediaRecorder(sourceStream, mediaRecorderOptions);
    mediaRecorder.ondataavailable = (event) => this.#pushAudioEventData(event.data);
    mediaRecorder.onerror = eventHandlers?.onerror ?? null;
    return mediaRecorder;
  }

  /**
   * Reinitializes the media recorder with a new media stream and options.
   */
  reinitMediaRecorder({
    sourceStream,
    mediaRecorderOptions,
    eventHandlers,
    shouldStartRecording,
  }: {
    sourceStream: MediaStream;
    mediaRecorderOptions?: MediaRecorderOptions;
    eventHandlers?: MediaRecorderEventHandlers;
    shouldStartRecording?: boolean;
  }) {
    return this.#getRecordingMutex.runExclusive(async () => {
      await this.#stopRecording({ shouldClearInterval: false });
      this.#resetRecordingMetadata();
      this.#initMediaRecorder(sourceStream, mediaRecorderOptions, eventHandlers);
      if (shouldStartRecording) {
        await this.#startRecording();
      }
      return this;
    });
  }

  /**
   * Initializes an interval that periodically gets the current audio events and
   * passes it to the `onIntervalProc` callback if available.
   */
  #initRecordingInterval(onIntervalProc: OnIntervalProc, intervalMs = RECORDED_AUDIO_INTERVAL_MS) {
    this.#clearRecordingInterval();
    this.#recordingInterval = setInterval(async () => {
      // Prevents multiple calls to getRecording from queuing up
      const isAlreadyGettingRecording = this.#getRecordingMutex.isLocked();
      const isRecording = this.getRecordingState() === RecordingState.RECORDING;
      if (isAlreadyGettingRecording || !isRecording) return;
      const recordingResult = await this.getRecording({
        shouldContinueRecordingAfter: true,
      });
      onIntervalProc(recordingResult);
    }, intervalMs);
  }

  /**
   * Clears the recording interval if it exists.
   */
  #clearRecordingInterval() {
    if (this.#recordingInterval) {
      clearInterval(this.#recordingInterval);
      this.#recordingInterval = null;
    }
  }

  getRecordingState() {
    return this.#mediaRecorder.state;
  }

  getRecordingMetadata() {
    return { ...this.#recordingMetadata };
  }

  getRecordingInterval() {
    return this.#recordingInterval;
  }

  #startRecording({ onIntervalProc, intervalMs }: StartRecordingOptions = {}) {
    return this.#controlRecordingMutex.runExclusive(() => {
      const isRecording = this.getRecordingState() === RecordingState.RECORDING;
      if (isRecording) return;
      this.#mediaRecorder.start();
      this.#recordingMetadata = {
        startTime: Date.now(),
        endTime: null,
      };
      if (onIntervalProc) {
        this.#initRecordingInterval(onIntervalProc, intervalMs ?? RECORDED_AUDIO_INTERVAL_MS);
      }
    });
  }

  /**
   * Starts recording audio and, if specified, kicks off an interval that
   * periodically gets recordings if an `onIntervalProc` callback is provided.
   * Idempotent and thread safe.
   */
  startRecording(options?: StartRecordingOptions) {
    return this.#getRecordingMutex.runExclusive(async () => await this.#startRecording(options));
  }

  #stopRecording(
    { shouldClearInterval }: StopRecordingOptions = {
      shouldClearInterval: true,
    },
  ) {
    return this.#controlRecordingMutex.runExclusive(() => {
      const isInactive = this.#mediaRecorder.state === RecordingState.INACTIVE;
      if (isInactive) return this.#audioEvents;
      return new Promise<AudioEventDataList>((resolve) => {
        this.#mediaRecorder.onstop = () => {
          this.#recordingMetadata.endTime = Date.now();
          const audioEventsCopy = [...this.#audioEvents];
          this.#clearAudioEvents();
          if (shouldClearInterval) {
            this.#clearRecordingInterval();
          }
          resolve(audioEventsCopy);
        };
        this.#mediaRecorder.stop();
      });
    });
  }

  /**
   * Returns a promise that stops the recording, clears the current audio
   * events, clears the recording interval, and resolves with a copy of the
   * aforementioned audio events after the recording has fully stopped.
   * Idempotent and thread safe.
   */
  stopRecording(options?: StopRecordingOptions) {
    return this.#getRecordingMutex.runExclusive(async () => await this.#stopRecording(options));
  }

  /**
   * Stops the recording and then consumes the recorded audio events to create
   * audio blob data. It won't start recording again unless otherwise
   * specified. Thread safe.
   */
  async getRecording({
    shouldContinueRecordingAfter,
  }: {
    shouldContinueRecordingAfter?: boolean;
  } = {}): Promise<RecordingResult> {
    // Stopping the recording is vital before consuming the audio events,
    // else the created blob may be empty
    return this.#getRecordingMutex.runExclusive(async () => {
      const audioEvents = await this.#stopRecording({
        shouldClearInterval: !shouldContinueRecordingAfter,
      });
      const audioBlob = new Blob(audioEvents);
      const audioURL = URL.createObjectURL(audioBlob);
      const recordingMetadata: RecordingMetadata = {
        ...this.#recordingMetadata,
        audioEventsCount: audioEvents.length,
        audioEventsSize: audioEvents.reduce((acc, event) => acc + event.size, 0),
        audioBlobSize: audioBlob.size,
        audioBlobType: audioBlob.type,
      };

      if (shouldContinueRecordingAfter) {
        await this.#startRecording();
      }

      return {
        audioBlob,
        audioURL,
        recordingMetadata,
      };
    });
  }
}
